import React, { useState, useEffect } from 'react';
import { useForm, useFieldArray } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FileText, Loader2, Save, Edit, Plus, X } from 'lucide-react';
import './MyForm.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPaste } from '@fortawesome/free-solid-svg-icons';
import { Helmet } from 'react-helmet-async';

// --- CUSTOM IMPORTS ---
import usePopup from '../hooks/usePopup';
import SuccessPopup from './SuccessPopup';
import ErrorPopup from './ErrorPopup';
// --- END CUSTOM IMPORTS ---

const REACT_APP_API_BASE = process.env.REACT_APP_API_BASE;

const experienceOptions = [
    "Fresher / No Experience",
    "Less than 1 year",
    "1-2 years",
    "2-5 years",
    "5-10 years",
    "10-15 years",
    "15-20 years",
    "20+ years"
];

const fatherEmploymentOptions = [
   { value: 'Homemaker', label: 'Homemaker' },
    { value: 'Employed', label: 'Employed' },
    { value: 'Self-employed / Business', label: 'Self-employed / Business' },
    { value: 'Retired', label: 'Retired' },
    { value: 'No longer with us', label: 'No longer with us' }
];
const motherEmploymentOptions = [
    { value: 'Homemaker', label: 'Homemaker' },
    { value: 'Employed', label: 'Employed' },
    { value: 'Self-employed / Business', label: 'Self-employed / Business' },
    { value: 'Retired', label: 'Retired' },
     { value: 'No longer with us', label: 'No longer with us' }
];

const financialRangeOptions = [
    { value: '', label: 'Select Range' },
    { value: 'Below 1 LPA', label: 'Below 1 LPA' },
    { value: '1 - 3 LPA', label: '1 - 3 LPA' },
    { value: '3 - 5 LPA', label: '3 - 5 LPA' },
    { value: '5 - 10 LPA', label: '5 - 10 LPA' },
    { value: '10 - 20 LPA', label: '10 - 20 LPA' },
    { value: '20 - 50 LPA', label: '20 - 50 LPA' },
    { value: 'Above 50 LPA', label: 'Above 50 LPA' }
];


const MyForm = () => {
    const navigate = useNavigate();
    const { popupState, showSuccessPopup, showErrorPopup, closePopup } = usePopup();

    const { register, handleSubmit, watch, setValue, reset, control, formState: { errors } } = useForm({
      defaultValues: {
        firstName: '',
        lastName: '',
        email: '',
        phoneNumber: '',
        pincode: '',
        city: '',
        district: '',
        state: '',
        percentage10th: '',
        percentage12th: '',
        stream12th: '',
        collegeCourse: '',
        collegePercentage: '',
        certifications: '',
        additionalCourses: [],
        hasMoreCourses: false,
        studentJobs: [],
        hasStudentJob: false,
        budget: '',
        bankBalance: { liquid: '', fd: '' },
        father: { employmentStatus: '', experience: '', designation: '', salary: '', businessType: '', revenue: '', hasPension: false, pensionAmount: '' },
        mother: { employmentStatus: '', experience: '', designation: '', salary: '', businessType: '', revenue: '', hasPension: false, pensionAmount: '' },
      }
    });
  
    const { fields: courseFields, append: appendCourse, remove: removeCourse } = useFieldArray({
      control,
      name: "additionalCourses"
    });
    
    const { fields: jobFields, append: appendJob, remove: removeJob } = useFieldArray({
      control,
      name: "studentJobs"
    });
  
    const hasMoreCourses = watch('hasMoreCourses');
    const fatherEmploymentStatus = watch('father.employmentStatus');
    const motherEmploymentStatus = watch('mother.employmentStatus');
    const hasStudentJob = watch('hasStudentJob');
    const fatherHasPension = watch('father.hasPension');
    const motherHasPension = watch('mother.hasPension');
  
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isPincodeLoading, setIsPincodeLoading] = useState(false);
    const [citiesList, setCitiesList] = useState([]);
    const [isInitialLoad, setIsInitialLoad] = useState(true);
    const [isEditMode, setIsEditMode] = useState(false);
    const [savedData, setSavedData] = useState({});
  
    const userId = localStorage.getItem('userId');
    const formExists = Object.keys(savedData).length > 0;
    const pincode = watch('pincode');

    useEffect(() => {
        if (hasStudentJob) {
            if (jobFields.length === 0) {
                appendJob({ jobTitle: '', companyName: '', experience: '', salary: '' });
            }
        } else {
            removeJob();
        }
    }, [hasStudentJob, jobFields.length, appendJob, removeJob]);

    useEffect(() => {
        const loadData = async () => {
          if (!userId) {
            setIsEditMode(true);
            setIsInitialLoad(false);
            return;
          }
          try {
            const formRes = await axios.get(`${REACT_APP_API_BASE}/api/form/${userId}`);
            if (formRes.data && Object.keys(formRes.data).length) {
              const fetchedData = {
                  ...formRes.data,
                  // Ensure studentJobs is always an array for react-hook-form
                  studentJobs: formRes.data.studentJobs || [],
              };
              setSavedData(fetchedData);
              reset(fetchedData); 
              setIsEditMode(false);
              
              if (fetchedData.pincode) {
                  axios.get(`https://api.postalpincode.in/pincode/${fetchedData.pincode}`)
                      .then(({ data }) => {
                          if (data[0].Status === 'Success') {
                              const uniqueCities = [...new Set(data[0].PostOffice.map(po => po.Name))].sort();
                              setCitiesList(uniqueCities);
                          }
                      }).catch(() => {});
              }
            } else {
              setIsEditMode(true);
            }
          } catch (error) {
            console.error("Error loading user data:", error);
            setIsEditMode(true);
          } finally {
            setIsInitialLoad(false);
          }
        };
        loadData();
      }, [userId, reset]);

    useEffect(() => {
        if (!pincode || !isEditMode || String(pincode).length !== 6) {
          if (isEditMode) { 
            setValue('city', '');
            setValue('district', '');
            setValue('state', '');
          }
          setCitiesList([]);
          return;
        }
      
        const timer = setTimeout(async () => {
          setIsPincodeLoading(true);
          try {
            const { data } = await axios.get(`https://api.postalpincode.in/pincode/${pincode}`);
            if (data[0].Status === 'Success') {
              const postOffices = data[0].PostOffice;
              const uniqueCities = [...new Set(postOffices.map(po => po.Name))].sort();
              setCitiesList(uniqueCities);
              setValue('district', postOffices[0].District);
              setValue('state', postOffices[0].State);
              if (uniqueCities.length === 1) setValue('city', uniqueCities[0]);
            } else {
              setCitiesList([]); setValue('city', ''); setValue('district', ''); setValue('state', '');
            }
          } catch { 
            setCitiesList([]); setValue('city', ''); setValue('district', ''); setValue('state', '');
          } finally { setIsPincodeLoading(false); }
        }, 500);
      
        return () => clearTimeout(timer);
      }, [pincode, isEditMode, setValue]);
  
    // --- UPDATED onSubmit FUNCTION ---
 const onSubmit = async (data) => {
  if (!userId) {
    showErrorPopup({ title: 'Not Logged In', message: 'You must be logged in to save your form.' });
    return;
  }

  const isFirstTimeSave = !formExists;
  setIsSubmitting(true);

  try {
    // 1) Clone and normalize
    const submissionData = { ...data };
    if (!submissionData.hasStudentJob) {
      submissionData.studentJobs = [];
    }

    // 2) Strip mongoose metadata
    delete submissionData._id;
    delete submissionData.__v;
    delete submissionData.createdAt;
    delete submissionData.updatedAt;

    // 3) POST to our freshly updated endpoint
    const payload = { ...submissionData, userId };
    console.log('🚀 payload →', payload);
    await axios.post(`${REACT_APP_API_BASE}/api/form/submit`, payload);

    // 4) Show success
    showSuccessPopup({
      title: isFirstTimeSave ? 'Saved!' : 'Updated!',
      message: `Your form has been ${isFirstTimeSave ? 'saved' : 'updated'} successfully.`,
      timer: 2000,
    });
    localStorage.setItem('hasForm', 'true');
    window.dispatchEvent(new Event('formSaved'));
    setSavedData(submissionData);
    setIsEditMode(false);

    // 5) Redirect on first save
    if (isFirstTimeSave) {
      setTimeout(() => navigate('/dashboard'), 1500);
    }
  }
  catch (err) {
    showErrorPopup({
      title: 'Save Failed',
      message: err.response?.data?.message || 'An unexpected error occurred. Please try again.'
    });
  }
  finally {
    setIsSubmitting(false);
  }
};



  if (isInitialLoad) {
    return <div className="form-page-container flex-center"><Loader2 className="animate-spin" size={48} /> <p>Loading your form...</p></div>;
  }

  return (
<>

 <Helmet>
          <meta name="title" content="My Form | Get Next Visa - AI Powered Student Visa Report" />
        <title>My Form | Get Next Visa - AI Powered Student Visa Report</title>
        <meta name="description" content="Fill out your comprehensive student profile to get personalized, AI-powered visa reports. Discover the best countries and colleges for your study abroad journey." />
      </Helmet>

    
    <div className="form-page-container">
       <SuccessPopup
            isOpen={popupState.isOpen && popupState.type === 'success'}
            onClose={closePopup}
            title={popupState.title}
            message={popupState.message}
            timer={popupState.timer}
        />
        <ErrorPopup
            isOpen={popupState.isOpen && popupState.type === 'error'}
            onClose={closePopup}
            title={popupState.title}
            message={popupState.message}
        />

      <div className="form-header-form">
        <h1 className="form-header-title-form">
          <FontAwesomeIcon className="form-icon" icon={faPaste} /> My Form
        </h1>
        <div className="form-header-actions">
          {!isEditMode && formExists && (
            <button type="button" className="form-edit-btn" onClick={() => setIsEditMode(true)}>
              <Edit size={18} />Edit Form
            </button>
          )}
          {isEditMode && (
            <button type="submit" form="student-application-form" className="form-submit-btn" disabled={isSubmitting}>
              {isSubmitting ? <Loader2 className="animate-spin" /> : <><Save size={18} /> {formExists ? 'Update' : 'Save'} Form</>}
            </button>
          )}
        </div>
      </div>
      
      <div className="form-content-area">
        <form id="student-application-form" onSubmit={handleSubmit(onSubmit)} className="student-form">
          {!isEditMode && formExists && (
            <div 
              className="form-overlay"
              onClick={() => showErrorPopup({
                title: 'Read-Only Mode',
                message: "Please click the 'Edit Form' button at the top to make changes.",
              })}
            />
          )}

          <div className="form-section">
              <h2 className="form-section-title">Personal Details</h2>
              <div className="form-grid">
                  <div className="form-group"><label>First Name</label><input readOnly={!isEditMode} {...register('firstName', { required: 'First Name is required' })} type='text' placeholder="John" />{errors.firstName && <p className="form-error">{errors.firstName.message}</p>}</div>
                  <div className="form-group"><label>Last Name</label><input readOnly={!isEditMode} {...register('lastName', { required: 'Last Name is required' })} placeholder="Doe" />{errors.lastName && <p className="form-error">{errors.lastName.message}</p>}</div>
                  <div className="form-group"><label>Email Address</label><input readOnly={!isEditMode} {...register('email', { required: 'Email is required', pattern: { value: /^\S+@\S+$/i, message: 'Invalid email address' } })} placeholder="user@example.com" />{errors.email && <p className="form-error">{errors.email.message}</p>}</div>
                  <div className="form-group"><label>Phone Number</label><input readOnly={!isEditMode} type="tel" maxLength="10" {...register('phoneNumber', { required: 'Phone number is required', pattern: { value: /^[0-9]{10}$/, message: 'Please enter a valid 10-digit number' } })} placeholder="e.g., 9876543210" />{errors.phoneNumber && <p className="form-error">{errors.phoneNumber.message}</p>}</div>
                  <div className="form-group pincode-group"><label>Pincode</label><input readOnly={!isEditMode} {...register('pincode', { required: 'Pincode is required', pattern: { value: /^[0-9]{6}$/, message: 'Must be 6 digits' } })} type="number" maxLength="6" placeholder="6-digit" />{isPincodeLoading && <Loader2 className="pincode-loader" />}{errors.pincode && <p className="form-error">{errors.pincode.message}</p>}</div>
                  <div className="form-group">
                      <label>City</label>
                      <select disabled={!isEditMode || isPincodeLoading || citiesList.length === 0} {...register('city', { required: 'City is required' })}>
                          <option value="">{isPincodeLoading ? 'Fetching...' : (citiesList.length > 0 ? 'Select City' : 'Enter Pincode')}</option>
                          {citiesList.map(city => <option key={city} value={city}>{city}</option>)}
                      </select>
                      {errors.city && <p className="form-error">{errors.city.message}</p>}
                  </div>
                  <div className="form-group"><label>District</label><input readOnly={true} {...register('district', { required: 'District is required' })} placeholder="Auto-filled" />{errors.district && <p className="form-error">{errors.district.message}</p>}</div>
                  <div className="form-group"><label>State</label><input readOnly={true} {...register('state', { required: 'State is required' })} placeholder="Auto-filled" />{errors.state && <p className="form-error">{errors.state.message}</p>}</div>
              </div>
          </div>

          <div className="form-section">
            <h2 className="form-section-title">Academic Background</h2>
            <div className="form-grid academic-grid">
              <div className="form-group"><label>10th Percentage</label><div className="input-with-adornment"><input readOnly={!isEditMode} type="number" step="0.01" {...register('percentage10th', { valueAsNumber: true, min: { value: 0, message: 'Cannot be negative' }, max: { value: 100, message: 'Cannot exceed 100' } })} placeholder="e.g., 92.4" /><span className="input-adornment">%</span></div>{errors.percentage10th && <p className="form-error">{errors.percentage10th.message}</p>}</div>
              <div className="form-group"><label>12th Percentage</label><div className="input-with-adornment"><input readOnly={!isEditMode} type="number" step="0.01" {...register('percentage12th', { valueAsNumber: true, min: { value: 0, message: 'Cannot be negative' }, max: { value: 100, message: 'Cannot exceed 100' } })} placeholder="e.g., 88.0" /><span className="input-adornment">%</span></div>{errors.percentage12th && <p className="form-error">{errors.percentage12th.message}</p>}</div>
              <div className="form-group"><label>12th Stream</label><select disabled={!isEditMode} {...register('stream12th')}><option value="">Select</option><option>Medical</option><option>Non-Medical</option><option>Commerce</option><option>Arts</option></select></div>
              <div className="form-group"><label>College Course Completed</label><input readOnly={!isEditMode} {...register('collegeCourse')} placeholder="e.g., B.Tech in CS" /></div>
              <div className="form-group"><label>College Percentage</label><div className="input-with-adornment"><input readOnly={!isEditMode} type="number" step="0.01" {...register('collegePercentage', { valueAsNumber: true, min: { value: 0, message: 'Cannot be negative' }, max: { value: 100, message: 'Cannot exceed 100' } })} placeholder="e.g., 85.5" /><span className="input-adornment">%</span></div>{errors.collegePercentage && <p className="form-error">{errors.collegePercentage.message}</p>}</div>
              <div className="form-group"><label>Any Certification</label><input readOnly={!isEditMode} {...register('certifications')} placeholder="e.g., AWS Certified Developer" /></div>
            </div>
            <div className="form-group-checkbox"><input type="checkbox" id="hasMoreCourses" {...register('hasMoreCourses')} disabled={!isEditMode} /><label htmlFor="hasMoreCourses">I have additional certificates or courses to list.</label></div>
            {hasMoreCourses && (
              <div className="additional-courses-container">
                <div className="additional-courses-header"><label>Certificate / Course Name</label><label>Marks / Percentage</label></div>
                {courseFields.map((field, index) => (
                  <div key={field.id} className="additional-course-row">
                    <div className="form-group"><input {...register(`additionalCourses.${index}.name`, { required: "Course name is required" })} readOnly={!isEditMode} placeholder="e.g., Advanced Data Structures" aria-label={`Course Name #${index + 1}`} />{errors.additionalCourses?.[index]?.name && <p className="form-error">{errors.additionalCourses[index].name.message}</p>}</div>
                    <div className="form-group"><div className="input-with-adornment"><input type="number" step="0.01" {...register(`additionalCourses.${index}.marks`, { valueAsNumber: true, min: { value: 0, message: 'Min 0' }, max: { value: 100, message: 'Max 100' } })} readOnly={!isEditMode} placeholder="e.g., 91.5" aria-label={`Marks for Course #${index + 1}`} /><span className="input-adornment">%</span></div>{errors.additionalCourses?.[index]?.marks && <p className="form-error">{errors.additionalCourses[index].marks.message}</p>}</div>
                    {isEditMode && (<button type="button" className="form-remove-btn" onClick={() => removeCourse(index)} aria-label={`Remove Course ${index + 1}`}><X size={16} /></button>)}
                  </div>
                ))}
                {isEditMode && (<button type="button" className="form-add-btn" onClick={() => appendCourse({ name: '', marks: '' })}><Plus size={18} /> Add Another</button>)}
              </div>
            )}
          </div>
          
          <div className="form-section">
              <h2 className="form-section-title">Financial & Family Details</h2>
              <div className="form-grid">
                  <div className="form-group"><label>Budget</label><select disabled={!isEditMode} {...register('budget')}><option value="">Select Budget</option><option value="Below 10 Lakhs">Below 10 Lakhs</option><option value="10 - 20 Lakhs">10 - 20 Lakhs</option><option value="20 - 30 Lakhs">20 - 30 Lakhs</option><option value="30 - 50 Lakhs">30 - 50 Lakhs</option><option value="Above 50 Lakhs">Above 50 Lakhs</option></select><p className="form-description">Your total estimated funds for the entire course.</p></div>
                  <div className="form-group"><label>Bank Balance (Liquid)</label><select disabled={!isEditMode} {...register('bankBalance.liquid')}><option value="">Select Balance</option><option value="Below 10 Lakhs">Below 10 Lakhs</option><option value="10 - 20 Lakhs">10 - 20 Lakhs</option><option value="20 - 30 Lakhs">20 - 30 Lakhs</option><option value="30 - 50 Lakhs">30 - 50 Lakhs</option><option value="50 Lakhs - 1 Cr">50 Lakhs - 1 Cr</option><option value="1 Cr - 5 Cr">1 Cr - 5 Cr</option><option value="Above 5 Cr">Above 5 Cr</option></select><p className="form-description">Money available in savings/current accounts.</p></div>
                  <div className="form-group"><label>Bank Balance (Fixed Deposit)</label><select disabled={!isEditMode} {...register('bankBalance.fd')}><option value="">Select Balance</option><option value="Below 10 Lakhs">Below 10 Lakhs</option><option value="10 - 20 Lakhs">10 - 20 Lakhs</option><option value="20 - 30 Lakhs">20 - 30 Lakhs</option><option value="30 - 50 Lakhs">30 - 50 Lakhs</option><option value="50 Lakhs - 1 Cr">50 Lakhs - 1 Cr</option><option value="1 Cr - 5 Cr">1 Cr - 5 Cr</option><option value="Above 5 Cr">Above 5 Cr</option></select><p className="form-description">Money held in Fixed Deposits (FDs).</p></div>
              </div>

              <h3 className="form-section-subtitle">Father's Details</h3>
              <div className="employment-status-group"><span id="father-status-label" className="radio-group-label">Employment Status</span><div role="radiogroup" aria-labelledby="father-status-label" className="radio-group">{fatherEmploymentOptions.map(opt => (<label key={`father-${opt.value}`} className={`radio-label ${fatherEmploymentStatus === opt.value ? 'selected' : ''}`}><input type="radio" {...register('father.employmentStatus')} value={opt.value} disabled={!isEditMode} />{opt.label}</label>))}</div></div>
              
              {(fatherEmploymentStatus === 'Employed' || fatherEmploymentStatus === 'Self-employed / Business') && (
                  <div className="conditional-fields-wrapper"><div className="form-grid">
                      {fatherEmploymentStatus === 'Employed' && (<>
                          <div className="form-group"><label>Experience</label><select disabled={!isEditMode} {...register('father.experience')}><option value="">Select Experience</option>{experienceOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}</select></div>
                          <div className="form-group"><label>Designation</label><input readOnly={!isEditMode} {...register('father.designation')} placeholder="e.g., Senior Manager" /></div>
                          <div className="form-group"><label>Salary (Yearly)</label><select disabled={!isEditMode} {...register('father.salary')}>{financialRangeOptions.map(opt => <option key={`father-salary-${opt.value}`} value={opt.value}>{opt.label}</option>)}</select></div>
                      </>)}
                      {fatherEmploymentStatus === 'Self-employed / Business' && (<>
                          <div className="form-group"><label>Type of Business</label><input readOnly={!isEditMode} {...register('father.businessType')} placeholder="e.g., Retail, Manufacturing" /></div>
                          <div className="form-group"><label>Yearly Revenue</label><select disabled={!isEditMode} {...register('father.revenue')}>{financialRangeOptions.map(opt => <option key={`father-revenue-${opt.value}`} value={opt.value}>{opt.label}</option>)}</select></div>
                      </>)}
                  </div></div>
              )}
              {fatherEmploymentStatus === 'Retired' && (
                  <div className="conditional-fields-wrapper">
                      <div className="form-group-checkbox pension-checkbox"><input type="checkbox" id="fatherHasPension" {...register('father.hasPension')} disabled={!isEditMode} /><label htmlFor="fatherHasPension">Receives a pension</label></div>
                      {fatherHasPension && (<div className="form-grid">
                          <div className="form-group"><label>Pension Amount (Yearly)</label><select disabled={!isEditMode} {...register('father.pensionAmount')}>{financialRangeOptions.map(opt => <option key={`father-pension-${opt.value}`} value={opt.value}>{opt.label}</option>)}</select></div>
                      </div>)}
                  </div>
              )}

              <h3 className="form-section-subtitle">Mother's Details</h3>
              <div className="employment-status-group"><span id="mother-status-label" className="radio-group-label">Employment Status</span><div role="radiogroup" aria-labelledby="mother-status-label" className="radio-group">{motherEmploymentOptions.map(opt => (<label key={`mother-${opt.value}`} className={`radio-label ${motherEmploymentStatus === opt.value ? 'selected' : ''}`}><input type="radio" {...register('mother.employmentStatus')} value={opt.value} disabled={!isEditMode} />{opt.label}</label>))}</div></div>

              {(motherEmploymentStatus === 'Employed' || motherEmploymentStatus === 'Self-employed / Business') && (
                  <div className="conditional-fields-wrapper"><div className="form-grid">
                      {motherEmploymentStatus === 'Employed' && (<>
                          <div className="form-group"><label>Experience</label><select disabled={!isEditMode} {...register('mother.experience')}><option value="">Select Experience</option>{experienceOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}</select></div>
                          <div className="form-group"><label>Designation</label><input readOnly={!isEditMode} {...register('mother.designation')} placeholder="e.g., Teacher" /></div>
                          <div className="form-group"><label>Salary (Yearly)</label><select disabled={!isEditMode} {...register('mother.salary')}>{financialRangeOptions.map(opt => <option key={`mother-salary-${opt.value}`} value={opt.value}>{opt.label}</option>)}</select></div>
                      </>)}
                      {motherEmploymentStatus === 'Self-employed / Business' && (<>
                          <div className="form-group"><label>Type of Business</label><input readOnly={!isEditMode} {...register('mother.businessType')} placeholder="e.g., Boutique, Consulting" /></div>
                          <div className="form-group"><label>Yearly Revenue</label><select disabled={!isEditMode} {...register('mother.revenue')}>{financialRangeOptions.map(opt => <option key={`mother-revenue-${opt.value}`} value={opt.value}>{opt.label}</option>)}</select></div>
                      </>)}
                  </div></div>
              )}
              {motherEmploymentStatus === 'Retired' && (
                  <div className="conditional-fields-wrapper">
                      <div className="form-group-checkbox pension-checkbox"><input type="checkbox" id="motherHasPension" {...register('mother.hasPension')} disabled={!isEditMode} /><label htmlFor="motherHasPension">Receives a pension</label></div>
                      {motherHasPension && (<div className="form-grid">
                          <div className="form-group"><label>Pension Amount (Yearly)</label><select disabled={!isEditMode} {...register('mother.pensionAmount')}>{financialRangeOptions.map(opt => <option key={`mother-pension-${opt.value}`} value={opt.value}>{opt.label}</option>)}</select></div>
                      </div>)}
                  </div>
              )}
              
              <h3 className="form-section-subtitle">Student's Work Experience</h3>
              <div className="form-group-checkbox"><input type="checkbox" id="hasStudentJob" {...register('hasStudentJob')} disabled={!isEditMode} /><label htmlFor="hasStudentJob">I have professional work experience.</label></div>
              
              {hasStudentJob && (
                <div className="conditional-fields-wrapper">
                  {jobFields.map((field, index) => (
                    <div key={field.id} className="dynamic-experience-row">
                      <div className="form-group">
                        <label>Job Title / Designation</label>
                        <input readOnly={!isEditMode} {...register(`studentJobs.${index}.jobTitle`)} placeholder="e.g., Software Engineer" />
                      </div>
                      <div className="form-group">
                        <label>Company Name</label>
                        <input readOnly={!isEditMode} {...register(`studentJobs.${index}.companyName`)} placeholder="e.g., Tech Solutions Inc." />
                      </div>
                      <div className="form-group">
                        <label>Years of Experience</label>
                        <select disabled={!isEditMode} {...register(`studentJobs.${index}.experience`)}><option value="">Select Experience</option>{experienceOptions.map(opt => <option key={`student-exp-${opt}`} value={opt}>{opt}</option>)}</select>
                      </div>
                      <div className="form-group">
                        <label>Salary (Yearly)</label>
                        <select disabled={!isEditMode} {...register(`studentJobs.${index}.salary`)}>{financialRangeOptions.map(opt => <option key={`student-salary-${opt.value}`} value={opt.value}>{opt.label}</option>)}</select>
                      </div>
                      {isEditMode && jobFields.length > 1 && (
                        <button type="button" className="form-remove-btn" onClick={() => removeJob(index)} aria-label={`Remove Job ${index + 1}`}>
                          <X size={16} />
                        </button>
                      )}
                    </div>
                  ))}
                  {isEditMode && (
                    <button type="button" className="form-add-btn" onClick={() => appendJob({ jobTitle: '', companyName: '', experience: '', salary: '' })}>
                      <Plus size={18} /> Add Another Job
                    </button>
                  )}
                </div>
              )}
          </div>
        </form>
      </div>
    </div>
    </>
  );
};

export default MyForm;